import {
  Divider,
  FormikInput,
  Link,
  LoadingButton,
  numberUtils,
  Stack,
  styled,
  Typography,
  useResponsive,
  useTheme
} from '@rabex-kit/rabex-ui';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';
import useInAppLink from '@/hooks/useInAppLink';
import PasswordChecklist from '../PasswordChecklist';
import { isValidPassword } from '@/utils/passwordRules';
import Confetti from '../../static/images/svg/Confetti.svg';
import { LOGIN } from '../../pages/routes';
import { RegisterRequestBody } from '../../services/auth/types';
import { emailRegex, mobileRegex } from '../Login';
import LoginWithGoogle from '../../containers/Login/google';
import { ClearButton, PasswordToggle } from '../AuthIconButton';

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  ...theme.typography.body2,
  '& > :not(style) ~ :not(style)': {
    marginTop: theme.spacing(2)
  }
}));

export type RegisterProps = {
  isLoading: boolean;
  onSubmit?: (values: RegisterRequestBody) => void;
  initialReferralCode?: string;
};

const RegisterForm: React.FC<RegisterProps> = ({
  onSubmit,
  isLoading,
  initialReferralCode = ''
}) => {
  const inAppLink = useInAppLink();
  const { matches } = useResponsive();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showCriteria, setShowCriteria] = useState(false);

  const handleSubmit = (values: RegisterRequestBody) => {
    if (values.phone && values.password) {
      // Validation already reads the number in ASCII digits; send it that way too.
      onSubmit &&
        onSubmit({ ...values, phone: numberUtils.toEn(values.phone).trim() });
    }
  };
  const validationSchema = yup.object({
    phone: yup
      .string()
      .required('ایمیل یا شماره همراه الزامی است.')
      .transform((value) => numberUtils.toEn(value))
      .test(
        'valid-email-or-mobile',
        'لطفاً یک ایمیل معتبر یا شماره همراه وارد کنید.',
        (value) => {
          if (!value) return false;
          return (
            emailRegex.test(value.trim()) || mobileRegex.test(value.trim())
          );
        }
      ),
    password: yup
      .string()
      .required('رمز عبور الزامی است.')
      // The checklist shows these same rules (utils/passwordRules).
      .test('valid-password', 'لطفاً یک رمز عبور معتبر وارد کنید.', (value) =>
        isValidPassword(value ?? '')
      ),
    parentReferralCode: yup.string().optional()
  });

  return (
    <Stack
      gap={0}
      width={{ xs: '100%', lg: 464 }}
      // mt={{ lg: 5, xs: 0 }}
      zIndex={2}>
      <Formik
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        initialValues={{
          phone: '',
          firstName: '',
          lastName: '',
          password: '',
          acceptTerms: true,
          parentReferralCode: initialReferralCode
        }}>
        {(formik) => (
          <Form>
            <Stack gap={2} mb={4}>
              <FormikInput
                value={formik.values.phone}
                title="برای ثبت‌نام شماره موبایل یا ایمیل خود را وارد کنید."
                size={matches({ xs: 'md', lg: 'lg' })}
                fill
                placeholder="شماره همراه یا ایمیل"
                name="phone"
                sx={{ mb: 2 }}
                endAdornment={
                  formik.values.phone && (
                    <ClearButton
                      onClick={() => {
                        formik.setFieldValue('phone', '');
                      }}
                    />
                  )
                }
              />

              <FormikInput
                value={formik.values.password}
                // Focus, not just a click: keyboard users and password managers see the rules too.
                onClick={() => {
                  setShowCriteria(true);
                }}
                onFocus={() => setShowCriteria(true)}
                size={matches({ xs: 'md', lg: 'lg' })}
                fill
                name="password"
                placeholder="رمز عبور"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                endAdornment={
                  <>
                    {formik.values.password && (
                      <ClearButton
                        onClick={() => {
                          formik.setFieldValue('password', '');
                        }}
                      />
                    )}
                    <PasswordToggle
                      shown={showPassword}
                      onToggle={() => setShowPassword(!showPassword)}
                    />
                  </>
                }
              />
              {showCriteria && (
                <PasswordChecklist
                  password={String(formik.values.password ?? '')}
                />
              )}
            </Stack>
            <Stack flexDirection="row" gap={1} mb={3}>
              <Typography
                variant="body2"
                weight="bold"
                color={theme.palette.textColor[900]}>
                کد معرف
              </Typography>
              <Typography variant="body2" color={theme.palette.textColor[400]}>
                (اختیاری)
              </Typography>
            </Stack>
            <FormikInput
              value={formik.values.parentReferralCode}
              size={matches({ xs: 'md', lg: 'lg' })}
              fill
              endAdornment={
                <>
                  {formik.values.parentReferralCode && (
                    <ClearButton
                      spacing="mr"
                      onClick={() => {
                        formik.setFieldValue('parentReferralCode', '');
                      }}
                    />
                  )}
                  <img src={Confetti} alt="" aria-hidden />
                </>
              }
              name="parentReferralCode"
              type="text"
              placeholder="کد معرف خود را وارد کنید"
            />
            <Typography
              variant="body2"
              sx={{ pt: 8, pb: 6 }}
              color={theme.palette.textColor[900]}>
              با ثبت‌نام&nbsp;
              {/* A new tab, so reading the terms doesn't throw away the half-filled form. */}
              <a
                href="https://rabex.ir/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary[500]
                }}>
                <b>شرایط و قوانین</b>&nbsp;
              </a>
              را می‌پذیرم.
            </Typography>
            <LoadingButton
              loading={isLoading}
              size={matches({ xs: 'md', lg: 'lg' })}
              disabled={
                formik.values.phone === '' || formik.values.password === ''
              }
              type="submit"
              fullWidth
              variant="contained">
              ایجاد حساب کاربری
            </LoadingButton>
            <Stack
              sx={{ my: matches({ lg: 6, xs: 2 }) }}
              direction="row"
              gap="12px">
              <Root>
                <Divider>
                  <Typography sx={{ px: 2 }}>یا</Typography>
                </Divider>
              </Root>
            </Stack>
            <Stack alignItems="center">
              <LoginWithGoogle text="signup_with" />
            </Stack>
          </Form>
        )}
      </Formik>
      <Link
        mt={matches({ lg: 6, xs: 4 })}
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
        {...inAppLink(LOGIN)}
        fontWeight="600"
        color={theme.palette.textColor[900]}
        sx={{ textDecoration: 'none' }}
        variant={matches({ xs: 'body3', lg: 'body2' })}>
        حساب کاربری دارید؟
        <Typography
          weight="bold"
          variant={matches({ lg: 'body2', xs: 'body3' })}
          color={theme.palette.primary[500]}>
          وارد شوید
        </Typography>
      </Link>
    </Stack>
  );
};

export default RegisterForm;
